import * as React from "react";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { Button, DataTable, type Column, Modal, ModalContent, Input, cn } from "@racoon/ui";
import { Globe, Search, Pencil, ArrowRight } from "lucide-react";

import { ConnectProto } from "@racoon/api";
import {
  useListAllUsersQuery,
  useUpdateUserMutation,
  roleEnumToKey,
} from "~/shared/api/user/user.grpc";
import { useListOrganizationsQuery } from "~/shared/api/organization/organization.grpc";

import { useImpersonateUserMutation } from "~/shared/api/admin-service/admin.api";
import { AUTH_STORAGE } from "@racoon/lib";

import { useUsersUrlState } from "../model/useUsersUrlState";
import { UserFormDialog } from "~/features/user-form/ui/user-form.dialog";
import { type RoleKey } from "~/shared/api/user/user.grpc";
import { useListNavigation } from "~/shared/hooks/use-list-navigation";
import { roleLabel } from "~/shared/lib/role-options";

export default function PlatformUsersPage() {
  const t = useT();

  const [addOpen, setAddOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<ConnectProto.User | null>(null);

  const [impersonate] = useImpersonateUserMutation();
  const [impersonatingId, setImpersonatingId] = React.useState<string | null>(null);
  const [customDialog, setCustomDialog] = React.useState<{
    isOpen: boolean;
    type: "confirm" | "alert";
    text: string;
    onConfirm?: () => void;
  } | null>(null);

  const getTargetAppUrl = (appType: "driver" | "client" | "audit") => {
    if (import.meta.env.DEV) {
      const ports = { driver: 8082, client: 8084, audit: 8085 };
      return `http://localhost:${ports[appType]}`;
    }
    const isTestEnv = window.location.hostname.includes("test");
    const domains = {
      driver: isTestEnv ? "driver-mobile.test" : "driver-temp",
      client: isTestEnv ? "client.test" : "client",
      audit: isTestEnv ? "audit.test" : "audit",
    };
    return `https://${domains[appType]}.raccoonai.kz`;
  };

  const safeStringify = (data: any) =>
    JSON.stringify(data, (key, value) => (typeof value === "bigint" ? value.toString() : value));

  const EXTERNAL_APPS_CONFIG: Record<
    string,
    { type: "driver" | "client" | "audit"; path: string; includeUser?: boolean }
  > = {
    USER_ROLE_DRIVER: { type: "driver", path: "/login?impersonate=true&token=" },
    USER_ROLE_RECEIVER_USER: { type: "client", path: "/impersonate?token=" },
    USER_ROLE_GOVERNMENT_AUDITOR: { type: "audit", path: "/impersonate?token=", includeUser: true },
    USER_ROLE_GOVERNMENT_AUDITOR_EDITOR: {
      type: "audit",
      path: "/impersonate?token=",
      includeUser: true,
    },
  };

  const handleImpersonate = React.useCallback(
    (user: ConnectProto.User) => {
      const userName = [user.firstName, user.lastName].filter(Boolean).join(" ");
      const defaultName = t(Namespace.Employees, "impersonate.defaultName" as TranslationKey);
      const promptText = t(Namespace.Employees, "impersonate.prompt" as TranslationKey, {
        name: userName || defaultName,
      });

      setCustomDialog({
        isOpen: true,
        type: "confirm",
        text: promptText,
        onConfirm: async () => {
          try {
            setImpersonatingId(String(user.id));
            const response = await impersonate({ id: user.id }).unwrap();
            const userToken = response.accessToken || (response as any).access_token;

            const externalApp = EXTERNAL_APPS_CONFIG[roleEnumToKey(user.role)];
            if (externalApp) {
              const baseUrl = getTargetAppUrl(externalApp.type);
              let url = `${baseUrl}${externalApp.path}${userToken}`;
              if (externalApp.includeUser && response.me) {
                url += `&user=${encodeURIComponent(safeStringify(response.me))}`;
              }
              window.open(url, "_blank");
            } else {
              const currentUserStr = localStorage.getItem(AUTH_STORAGE.user);
              const currentToken = localStorage.getItem(AUTH_STORAGE.token);
              const currentTokenType = localStorage.getItem(AUTH_STORAGE.tokenType);

              if (currentUserStr) {
                const currentUser = JSON.parse(currentUserStr);
                if (currentUser.role === "USER_ROLE_ADMIN") {
                  localStorage.setItem("admin_backup_token", currentToken || "");
                  localStorage.setItem("admin_backup_token_type", currentTokenType || "");
                  localStorage.setItem("admin_backup_user", currentUserStr);
                }
              }

              const tokenType = response.tokenType || (response as any).token_type || "Bearer";
              localStorage.setItem(AUTH_STORAGE.token, userToken);
              localStorage.setItem(AUTH_STORAGE.tokenType, tokenType);
              if (response.me) {
                localStorage.setItem(AUTH_STORAGE.user, safeStringify(response.me));
              }
              window.location.replace("/");
            }
          } catch (error) {
            console.error("Impersonate failed:", error);
            alert(t(Namespace.Employees, "impersonate.error" as TranslationKey));
          } finally {
            setImpersonatingId(null);
          }
        },
      });
    },
    [impersonate, t],
  );

  const [updateUser] = useUpdateUserMutation();
  const { data: userList, isLoading } = useListAllUsersQuery();
  const users = userList?.users ?? [];

  const { data: orgData } = useListOrganizationsQuery({});
  const orgMap = React.useMemo(() => {
    const m = new Map<string, string>();
    (orgData?.organizations ?? []).forEach((o) => m.set(String(o.id), o.name));
    return m;
  }, [orgData]);

  const {
    states,
    selectedState,
    onStateChange,
    searchValue,
    onSearchChange,
    items: filteredItems,
  } = useUsersUrlState({ data: users, orgMap });

  type Row = ConnectProto.User;
  const columns = React.useMemo<Column<Row>[]>(
    () => [
      {
        id: "idx",
        header: t(Namespace.Employees, "table.index"),
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "orgName",
        header: t(Namespace.Employees, "table.organization"),
        accessor: (row: Row) => orgMap.get(String(row.ownerOrganizationId)) || "",
        width: 200,
        cell: (info: any) => {
          const row = info.row.original as ConnectProto.User;
          const label = orgMap.get(String(row.ownerOrganizationId)) || "—";
          return (
            <button
              type="button"
              className="text-left text-brand-blue hover:opacity-70 transition-opacity truncate"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(row);
                setEditOpen(true);
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        id: "fio",
        header: t(Namespace.Employees, "table.fullName"),
        accessor: (row: Row) => [row.firstName, row.lastName].filter(Boolean).join(" "),
        width: 250,
        cell: (info: any) => {
          const row = info.row.original;
          return [row.firstName, row.lastName].filter(Boolean).join(" ");
        },
      },
      {
        id: "position",
        header: t(Namespace.Employees, "table.position"),
        accessor: (row: Row) => roleLabel(t, row.role),
        width: 180,
        cell: (info: any) => roleLabel(t, info.row.original.role),
      },
      {
        id: "driverLicense",
        header: t(Namespace.Employees, "table.driverLicense"),
        accessor: (row: Row) => row.driverLicenseNumber ?? "",
        width: 160,
        cell: (info: any) => info.getValue() || "—",
      },
      {
        id: "phone",
        header: t(Namespace.Employees, "table.phone"),
        key: "phoneNumber",
        width: 180,
        cell: (info: any) => info.getValue() ?? "-",
      },
      {
        id: "email",
        header: t(Namespace.Employees, "table.email"),
        key: "email",
        width: 200,
        cell: (info: any) => <span>{info.getValue() || "—"}</span>,
      },
      {
        id: "actions",
        header: t(Namespace.Employees, "table.actions"),
        accessor: () => "",
        width: 80,
        cell: (info: any) => {
          const row = info.row.original as ConnectProto.User;
          return (
            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedUser(row);
                  setEditOpen(true);
                }}
                className="w-6 h-6 rounded-lg bg-[#d6e6ff] flex items-center justify-center shrink-0"
              >
                <Pencil className="w-3 h-3 text-[#007aff]" />
              </button>
              {row.status !== ConnectProto.UserStatus.INACTIVE && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImpersonate(row);
                  }}
                  disabled={impersonatingId === String(row.id)}
                  className="w-6 h-6 rounded-lg bg-[#007aff] flex items-center justify-center shrink-0 disabled:opacity-30"
                >
                  <ArrowRight className="w-3 h-3 text-white" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [t, handleImpersonate, impersonatingId, setSelectedUser, setEditOpen, orgMap],
  );

  const defaultRole: RoleKey | undefined = React.useMemo(() => {
    const tabValue = selectedState?.value;
    if (tabValue === "dispatchers") return "USER_ROLE_PROVIDER_DISPATCHER";
    if (tabValue === "drivers") return "USER_ROLE_DRIVER";
    if (tabValue === "mechanics") return "USER_ROLE_PROVIDER_MECHANIC";
    if (tabValue === "support") return "USER_ROLE_SUPPORT";
    if (tabValue === "management") return "USER_ROLE_PROVIDER_OWNER";
    if (tabValue === "sales") return "USER_ROLE_PROVIDER_SALES";
    return undefined;
  }, [selectedState]);

  const navigation = useListNavigation(filteredItems, selectedUser, setSelectedUser);

  const handleChangeUserStatus = async (newStatus: ConnectProto.UserStatus, confirmKey: string) => {
    if (!selectedUser) return;
    const userName = [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(" ");
    const confirmed = window.confirm(t(Namespace.Employees, confirmKey as any, { userName }));
    if (!confirmed) return;
    try {
      await updateUser({ id: selectedUser.id, status: newStatus }).unwrap();
      setSelectedUser(null);
    } catch (error) {
      console.error(`Failed to change user status to ${newStatus}:`, error);
    }
  };

  const handleArchiveUser = () =>
    handleChangeUserStatus(ConnectProto.UserStatus.INACTIVE, "archive.confirm");
  const handleRestoreUser = () =>
    handleChangeUserStatus(ConnectProto.UserStatus.ACTIVE, "restore.confirm");

  const isUserArchived = selectedUser?.status === ConnectProto.UserStatus.INACTIVE;

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1">
      {/* Filter header: tabs + search + action buttons */}
      <div className="flex-none flex flex-col gap-3 p-4">
        {/* Filter chips */}
        <div className="flex max-w-full gap-2 flex-wrap">
          {states.map((state) => {
            const active = selectedState?.value === state.value;
            return (
              <button
                key={state.value}
                type="button"
                onClick={() => onStateChange(state)}
                className={cn(
                  "inline-flex items-center h-6 px-2 rounded-full shrink-0 transition-colors",
                  active ? "bg-brand-blue text-white" : "bg-brand-blue-light text-brand-blue",
                )}
              >
                <span className="text-xs font-medium whitespace-nowrap">{state.key}</span>
              </button>
            );
          })}
        </div>

        {/* Search + action buttons row */}
        <div className="flex flex-nowrap items-center gap-3">
          <div className="relative shrink-0" style={{ width: 500 }}>
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              size="sm"
              placeholder={t(Namespace.Employees, "searchPlaceholder")}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 !rounded-lg w-full"
            />
          </div>

          {selectedUser && (
            <>
              {!isUserArchived && (
                <Button
                  variant="opacityBlueWithOutline"
                  size="sm"
                  className="inline-flex items-center gap-2"
                  onClick={() => setEditOpen(true)}
                >
                  {t(Namespace.Common, "edit") || "Редактировать"}
                </Button>
              )}
              {isUserArchived ? (
                <Button
                  variant="opacityBlueWithOutline"
                  size="sm"
                  className="inline-flex items-center gap-2"
                  onClick={handleRestoreUser}
                >
                  {t(Namespace.Employees, "restore.button") || "Восстановить"}
                </Button>
              ) : (
                <Button
                  variant="opacityRedWithOutline"
                  size="sm"
                  className="inline-flex items-center gap-2"
                  onClick={handleArchiveUser}
                >
                  {t(Namespace.Employees, "archive.button") || "Архивировать"}
                </Button>
              )}
            </>
          )}

          <button
            id="users-add-button"
            type="button"
            className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-brand-blue-light text-brand-blue text-sm font-medium whitespace-nowrap hover:bg-[#c0d8ff] transition-colors"
            onClick={() => setAddOpen(true)}
          >
            {t(Namespace.Employees, "add")}
          </button>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-200 -mx-4" />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden px-4 pb-4">
        <DataTable<Row>
          className="h-full overflow-y-auto"
          columns={columns}
          data={filteredItems}
          loading={isLoading}
          initialSort={{ id: "orgName", dir: "asc" }}
          rowKey={(r) => String(r.id)}
          selectable
          selectedRowKeys={selectedUser ? [String(selectedUser.id)] : []}
          onSelectionChange={(keys) => {
            if (keys.length > 0) {
              const userId = keys[0];
              const user = filteredItems.find((u) => String(u.id) === String(userId));
              setSelectedUser(user || null);
            } else {
              setSelectedUser(null);
            }
          }}
          caption={null}
          zebra
          rowClassName={() => "group hover:!bg-gray-100"}
        />
      </div>

      <UserFormDialog
        open={addOpen || editOpen}
        onOpenChange={(v) => {
          if (!v) {
            setAddOpen(false);
            setEditOpen(false);
          }
        }}
        user={editOpen ? selectedUser : null}
        org={editOpen ? { mode: "readonly", orgMap } : { mode: "picker" }}
        defaultRole={defaultRole}
        onSaved={() => {
          if (editOpen) setSelectedUser(null);
        }}
        navigation={navigation}
      />

      <Modal
        open={customDialog?.isOpen || false}
        onOpenChange={(isOpen) => !isOpen && setCustomDialog(null)}
      >
        <ModalContent className="max-w-[400px] p-6 bg-white rounded-[12px] shadow-xl border-0 !gap-0">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={18} className="text-gray-800" />
            <span className="font-semibold text-gray-900 text-[15px]">
              {typeof window !== "undefined" ? window.location.host : ""}
            </span>
          </div>
          <div className="text-gray-800 text-[15px] mb-8">{customDialog?.text}</div>
          <div className="flex justify-end gap-3">
            {customDialog?.type === "confirm" && (
              <Button
                onClick={() => setCustomDialog(null)}
                className="!bg-white !text-gray-800 border border-gray-300 hover:!bg-gray-50 shadow-sm font-medium px-4 h-9 rounded-md transition-colors"
              >
                {t(Namespace.Operators, "cancel" as TranslationKey)}
              </Button>
            )}
            <Button
              onClick={() => {
                const action = customDialog?.onConfirm;
                setCustomDialog(null);
                if (action) action();
              }}
              className="!bg-[#0066FF] !text-white hover:!bg-blue-600 shadow-sm font-medium px-4 h-9 rounded-md transition-colors"
            >
              OK
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}
